import React from 'react';
import { Icon } from '@eventbrite/legacy-eds.icon';
import {
    PlusChunky as PlusChunkySvg,
    UserChunky as UserChunkySvg,
} from '@eventbrite/legacy-eds.iconography';
import { ROUTER_LINK } from '@eventbrite/legacy-eds.nav-list-item/constants';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
// TODO: This used to use Link from react-router-dom but we removed it as a hard dependency
// import { Link } from 'react-router-dom';
import type { MenuInfoShape } from '@eventbrite/consumer-header.global-nav/types';
import { DropdownButton, DropdownComponentByType, GlobalHeaderMenuIcon } from '../common';
import styles from './GlobalHeaderNavigationBar.module.scss';
import { useIntl, type MessageDescriptor } from 'react-intl';

// Anchor needs to be a React component in order to re-render properly
const AnchorLink = (props: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
    <a {...props}>{props.children}</a>
);

export const DEFAULT_ICON_COLOR = 'grey-700';

export const GlobalHeaderQuickLinks = ({
    links,
    shouldShowIcon,
}: {
    links:
        | {
              url: string;
              content: string | React.ReactNode | MessageDescriptor;
              iconType: React.ReactNode;
              narrowIconType?: React.ReactNode;
              narrowIconColor?: string;
              iconColor?: string;
              isActive?: boolean;
              type?: string;
              restProps?: any;
          }[]
        | null;
    shouldShowIcon: boolean;
}) => {
    const { formatMessage } = useIntl();
    if (!links) {
        return null;
    }
    const linkComponents = links.map(
        ({
            url,
            content,
            narrowIconType,
            iconType,
            narrowIconColor,
            iconColor,
            isActive,
            type,
            ...restProps
        }) => {
            let narrowIcon: JSX.Element | null = null;

            let Component: any = AnchorLink;
            const linkProps: any = restProps;
            const quickLinkWrapperClassName = classNames(styles.quickLink, {
                // If the link collapses into an icon on narrow screens,
                // we want to make sure we're not hiding it
                [styles.quickLinkNarrow as string]: narrowIconType,
                // If link is active apply this class
                [styles.quickLinkActive as string]: isActive,
            });

            let icon: JSX.Element | null = null;

            if (shouldShowIcon) {
                icon = <Icon size="small" type={iconType} color={iconColor} />;
            }

            if (narrowIconType) {
                narrowIcon = (
                    <GlobalHeaderMenuIcon
                        iconType={narrowIconType}
                        iconColor={narrowIconColor || ''}
                    />
                );
            }

            if (type === ROUTER_LINK && linkProps.to) {
                // TODO: This used to use Link from react-router-dom but we removed it as a hard dependency
                Component = AnchorLink;
                // Component = Link;
                linkProps.to = url;
            } else {
                linkProps.href = url;
            }

            let _content: React.ReactNode;
            if (typeof content === 'string') {
                _content = content;
            } else if (content && typeof content === 'object' && 'defaultMessage' in content) {
                _content = formatMessage(content);
            } else {
                _content = content as React.ReactNode;
            }
            return (
                <Component
                    {...linkProps}
                    key={url}
                    className={quickLinkWrapperClassName}
                    aria-label={_content}
                >
                    <span>{icon || narrowIcon}</span>
                    <span>{_content}</span>
                </Component>
            );
        },
    );

    return (
        <div className={styles.quickLinks} data-testid="consumer-header-quick-links">
            {linkComponents}
        </div>
    );
};

export const GlobalHeaderCallToAction = ({
    linkInfo,
    iconType,
    shouldShowIcon,
}: {
    linkInfo: {
        url: string;
        content: JSX.Element;
        linkProps: any;
    };
    iconColor?: string;
    iconType?: JSX.Element;
    shouldShowIcon: boolean;
}) => {
    let component: JSX.Element | null = null;

    if (linkInfo) {
        const { url, content, ...linkProps } = linkInfo;
        let icon: JSX.Element | null = null;

        if (shouldShowIcon) {
            icon = iconType ? iconType : <Icon size="small" type={<PlusChunkySvg />} />;
        }

        component = (
            <a
                {...linkProps}
                href={url}
                data-testid="consumer-header-call-to-action"
                className={classNames(styles.quickLink, styles.menuLabel, styles.createEventLink)}
            >
                {icon}
                {content}
            </a>
        );
    }

    return component;
};

export const GlobalHeaderMenu = ({
    menuInfo,
    onAdditionalMenuTextItemSelect,
    noLabel = false,
    onClickDropdown,
    ...additionalProps
}: {
    menuInfo: MenuInfoShape;
    noLabel?: boolean;
    narrowIconType?: React.ReactNode;
    narrowIconColor?: string;
    onAdditionalMenuTextItemSelect?: Function;
    onClickDropdown?: Function;
    additionalProps?: any;
}) => {
    let component: JSX.Element | null = null;

    if (menuInfo) {
        const { textItems = [], iconType, iconColor, narrowIconType, narrowIconColor } = menuInfo;
        const hasOnlyOneItem = textItems.length === 1;

        if (hasOnlyOneItem) {
            const item = textItems[0] as { value: string; content: JSX.Element };

            component = (
                <DropdownButton
                    item={item}
                    iconType={iconType}
                    iconColor={iconColor || DEFAULT_ICON_COLOR}
                    narrowIconType={narrowIconType}
                    narrowIconColor={narrowIconColor}
                    onClick={
                        onAdditionalMenuTextItemSelect ||
                        (() => {
                            return;
                        })
                    }
                />
            );
        } else {
            component = (
                <DropdownComponentByType
                    {...additionalProps}
                    onClickDropdown={onClickDropdown}
                    menuInfo={menuInfo}
                    noLabel={noLabel}
                    onAdditionalMenuTextItemSelect={onAdditionalMenuTextItemSelect}
                />
            );
        }
    }

    return component;
};

export const GlobalHeaderMenus = ({
    userMenuInfo,
    additionalMenus,
    onAdditionalMenuTextItemSelect,
    onClickDropdown,
}: {
    userMenuInfo?: any;
    additionalMenus?: MenuInfoShape[];
    onAdditionalMenuTextItemSelect?: Function;
    onClickDropdown?: Function;
}) => {
    let menus: React.ReactNode[] = [];

    let component: JSX.Element | null = null;

    if (!isEmpty(additionalMenus) && additionalMenus) {
        menus = additionalMenus?.map((menuInfo) => (
            <GlobalHeaderMenu
                key={menuInfo.fallbackUrl}
                menuInfo={menuInfo}
                onAdditionalMenuTextItemSelect={onAdditionalMenuTextItemSelect}
            />
        ));
    }
    if (userMenuInfo) {
        const userMenuInfoWithIcon = {
            iconType: <UserChunkySvg />,
            className: styles.userMenu,
            ...userMenuInfo,
        };

        menus = [
            ...menus,
            <GlobalHeaderMenu
                key="userMenu"
                menuInfo={userMenuInfoWithIcon}
                data-testid="consumer-header-user-menu"
                onClickDropdown={onClickDropdown}
            />,
        ];
    }

    if (!isEmpty(menus)) {
        component = (
            <div className={styles.menus} data-testid="consumer-header-menus">
                {menus}
            </div>
        );
    }

    return component;
};
